import { useCallback, useRef } from 'react';

import { usePopup } from '@/features/Popup/usePopup';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import {
  DYNAMIC_POPUP_TYPES,
  GR_TRACKING_EVENT,
} from '@/features/Popup/constants';
import {
  GEOLOCATION_STEPS,
  GeolocationStepsType,
  getPopupType,
} from '@/features/Popup/types';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { executeQueue } from '@/store/actions';
import { useStore } from '@/store/useStore';

// The refresh popup may be queued behind a higher-priority popup (reality check,
// exit, …). Only tear it down when it is the one actually on screen, so a step
// transition never dismisses an unrelated modal or reorders the queue.
const isRefreshPopupPresenting = () =>
  useStore.getState().presentingType ===
  DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH;

const trackGrEvent = (
  event: (typeof GR_TRACKING_EVENT)[keyof typeof GR_TRACKING_EVENT],
) =>
  Logger.logRumAction({
    name: `GR_TRACKING_ACTIONS_${event}`,
    useCase: DdEventCategory.Geolocation,
    eventType: DdEventType.Info,
    service: 'geolocation-web',
  });

/**
 * Drives the 3-step GEOLOCATION_REFRESH popup shown when geolocation is
 * invalidated mid-game:
 *  - `showRefreshing` presents the LOADING step (game paused).
 *  - `showRefreshSuccess` swaps to the SUCCESS step and auto-closes after the
 *    step's configured `autoClose.ms`.
 *  - `showRefreshFailed` swaps to the ERROR step.
 *
 * Success/error transitions are no-ops (and return `false`) unless a refresh is
 * actually in progress, so ordinary first-time geolocation passes/failures are
 * unaffected. The success/failed helpers return whether they handled the event
 * so callers can suppress the full-screen error screen for a mid-game refresh.
 */
export const useGeolocationRefreshPopup = () => {
  const { showPopup, hidePopup } = usePopup();
  const refreshInProgress = useRef(false);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const present = useCallback(
    (step: GeolocationStepsType) => {
      // Each step is a separate popup in the display queue, which appends and
      // presents one popup of a type at a time. The refresh popup is a
      // singleton: GeoComply streams invalidated/failure events, so ALWAYS
      // dismiss the refresh popup that is currently on screen before queuing the
      // next step — otherwise every event appends another entry and the user has
      // to close them one by one. Only dismiss when OUR popup is the one
      // presenting, so a higher-priority modal is never torn down. Then nudge
      // the queue so the new step surfaces immediately instead of waiting for
      // the poll interval.
      if (isRefreshPopupPresenting()) hidePopup();
      const config = generatePopupConfig(
        DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH,
        { step },
      );
      showPopup(config, DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH);
      executeQueue();
    },
    [showPopup, hidePopup],
  );

  const showRefreshing = useCallback(() => {
    // GeoComply can emit 'invalidated' several times for one refresh. The
    // LOADING step is a singleton, so ignore repeats while a refresh is already
    // in progress — otherwise each event queues another LOADING popup and the
    // user has to close them one by one. Success/error clear the flag, so a
    // genuine later re-invalidation still shows the loader again.
    if (refreshInProgress.current) return;
    refreshInProgress.current = true;
    trackGrEvent(GR_TRACKING_EVENT.INVALIDATED_MODAL_VIEWED);
    present(GEOLOCATION_STEPS.LOADING);
  }, [present]);

  const showRefreshSuccess = useCallback(() => {
    if (!refreshInProgress.current) return false;
    refreshInProgress.current = false;

    trackGrEvent(GR_TRACKING_EVENT.SUCCESS_MODAL_VIEWED);
    present(GEOLOCATION_STEPS.SUCCESS);

    const { autoClose } =
      getPopupType(DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH).dynamicProps?.[
        GEOLOCATION_STEPS.SUCCESS
      ] ?? {};

    if (autoClose?.ms) {
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = setTimeout(() => {
        // Only auto-dismiss if our SUCCESS popup is still the one on screen; a
        // higher-priority popup may have surfaced during the auto-close window.
        if (isRefreshPopupPresenting()) hidePopup();
      }, autoClose.ms);
    }

    return true;
  }, [present, hidePopup]);

  const showRefreshFailed = useCallback(() => {
    if (!refreshInProgress.current) return false;
    refreshInProgress.current = false;

    trackGrEvent(GR_TRACKING_EVENT.FAIL_MODAL_VIEWED);
    present(GEOLOCATION_STEPS.ERROR);

    return true;
  }, [present]);

  return { showRefreshing, showRefreshSuccess, showRefreshFailed };
};
